import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";

import { markOrderSeen } from "../alerts";
import { api, ApiError, type Me, type QueueLine, type QueueOrder } from "../api";
import { REJECT_REASONS, STATUS_LABEL, taka } from "../format";
import { acceptTimer, areaLabel, C, card, eyebrow, font, payLabel, pill, slotLabel, unitsPerStrip, useNow } from "../pharmacy/look";
import { SubstituteDialog, type Chosen } from "../pharmacy/SubstituteDialog";
import { RxPhoto } from "../RxPhoto";

// P2 · Order card + prescription check: the prescription (when there is one)
// beside the checks and the medicines, each marked on shelf, unavailable or
// substitute; delivery details once confirmed; reject or confirm.

type Decision = { status: "on_shelf" } | { status: "unavailable" } | { status: "substitute"; chosen: Chosen };

const CHECKS = [
  "Doctor's name and registration number are readable",
  "Dated within the last 6 months",
  "Signed by the doctor",
  "Medicines and strengths match this order",
];

export default function PharmacyOrder({ orderId, me }: { orderId: string; me: Me }) {
  const qc = useQueryClient();
  const now = useNow();
  const order = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => api<QueueOrder>(`/pharmacy/orders/${orderId}`),
    refetchInterval: 15_000,
  });
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});
  const [checks, setChecks] = useState<boolean[]>(CHECKS.map(() => false));
  const [subFor, setSubFor] = useState<QueueLine | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Opening the order stops its new-order alert.
  useEffect(() => markOrderSeen(orderId), [orderId]);

  const act = useMutation({
    mutationFn: (p: { path: string; body: unknown }) => api<QueueOrder>(`/pharmacy/orders/${orderId}/${p.path}`, { body: p.body }),
    onSuccess: (data) => {
      qc.setQueryData(["order", orderId], data);
      void qc.invalidateQueries({ queryKey: ["queue"] });
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? explain(err.code) : "Could not save. Try again."),
  });

  if (order.isLoading) return <div style={{ fontFamily: font.sans, color: C.muted }}>Loading…</div>;
  if (!order.data) return <div style={{ fontFamily: font.sans, color: C.danger }}>Order not found.</div>;
  const o = order.data;
  const editable = o.status === "placed";
  const timer = acceptTimer(o, now);
  const pharmacyName = me.pharmacy?.name ?? "The pharmacy";
  const allDecided = o.lines.every((l) => decisions[l.lineId]);
  const checksDone = !o.hasPrescription || checks.every(Boolean);
  const anySubstitute = Object.values(decisions).some((d) => d.status === "substitute");

  const confirm = () => {
    const lines = o.lines.map((l) => {
      const d = decisions[l.lineId]!;
      return d.status === "substitute"
        ? { lineId: l.lineId, status: "substitute", substituteMedicineId: d.chosen.medicine.medicineId, substituteUnitPricePaisa: d.chosen.unitPricePaisa }
        : { lineId: l.lineId, status: d.status };
    });
    act.mutate({ path: "confirm", body: { lines } });
  };

  return (
    <div style={{ fontFamily: font.sans, color: C.ink, display: "flex", flexDirection: "column", gap: 20 }}>
      <Link href="/queue" className="no-print" style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, minHeight: 44, fontSize: 15, fontWeight: 600, textDecoration: "none", color: C.ink }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 6l-6 6 6 6" />
        </svg>
        All orders
      </Link>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <h1 style={{ margin: 0, fontSize: 26, lineHeight: "32px", fontWeight: 700, letterSpacing: "-0.015em" }}>Order {o.orderId}</h1>
          <div style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>
            {payLabel(o.paymentMethod)} · {areaLabel(o.area, o.zone)} · Deliver {slotLabel(o.deliverBy).replace(/^T/, "t")} · {taka(o.totalPaisa)}
          </div>
        </div>
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            padding: "8px 14px",
            borderRadius: 999,
            border: `2px solid ${timer?.late ? C.danger : C.teal}`,
            background: C.surface,
            color: timer?.late ? C.danger : C.ink,
          }}
        >
          {timer?.text ?? STATUS_LABEL[o.status] ?? o.status}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
        {o.hasPrescription && (
          <section aria-label="Prescription" style={{ ...card, flex: "1 1 380px", minWidth: 0, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18, lineHeight: "24px", fontWeight: 700 }}>Prescription</h2>
            <RxPhoto orderId={o.orderId} />
          </section>
        )}

        <div style={{ flex: "999 1 480px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>
          {o.hasPrescription && editable && (
            <section aria-labelledby="check-h" style={{ ...card, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
              <h2 id="check-h" style={{ margin: "0 0 6px", fontSize: 18, lineHeight: "24px", fontWeight: 700 }}>
                Check the prescription
              </h2>
              {CHECKS.map((label, i) => (
                <label key={label} style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 44, fontSize: 15, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={checks[i]}
                    onChange={(e) => setChecks((c) => c.map((v, j) => (j === i ? e.target.checked : v)))}
                    style={{ width: 22, height: 22, margin: 0, accentColor: C.teal }}
                  />
                  {label}
                </label>
              ))}
            </section>
          )}

          <section aria-labelledby="items-h" style={{ ...card, padding: "16px 20px", display: "flex", flexDirection: "column" }}>
            <h2 id="items-h" style={{ margin: "0 0 6px", fontSize: 18, lineHeight: "24px", fontWeight: 700 }}>
              Medicines
            </h2>
            {o.lines.map((l, i) => (
              <LineRow
                key={l.lineId}
                line={l}
                last={i === o.lines.length - 1}
                editable={editable}
                decision={decisions[l.lineId]}
                onPick={(status) => {
                  if (status === "substitute") setSubFor(l);
                  else setDecisions((d) => ({ ...d, [l.lineId]: { status } }));
                }}
              />
            ))}
          </section>

          <section aria-label="Delivery" style={{ ...card, padding: "16px 20px", display: "flex", flexWrap: "wrap", gap: "12px 32px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={eyebrow}>DELIVER TO</span>
              <span style={{ fontSize: 15, lineHeight: "22px" }}>
                {o.contact ? o.contact.address : `${areaLabel(o.area, o.zone)} · full address after you confirm`}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={eyebrow}>CUSTOMER PHONE</span>
              <span style={{ fontSize: 15, lineHeight: "22px" }}>
                {o.contact ? <a href={`tel:${o.contact.phone}`}>{o.contact.phone}</a> : "Shown after you confirm"}
              </span>
            </div>
          </section>

          {error && <div style={{ color: C.danger, fontSize: 15 }}>{error}</div>}

          <div className="no-print" style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 12 }}>
            {(o.status === "placed" || o.status === "awaiting_customer" || o.status === "confirmed") &&
              (rejecting ? (
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>Why?</span>
                  {REJECT_REASONS.map((r) => (
                    <button key={r.code} disabled={act.isPending} onClick={() => act.mutate({ path: "reject", body: { reason: r.code } })} style={{ ...pill("secondary"), color: C.danger }}>
                      {r.label}
                    </button>
                  ))}
                  <button onClick={() => setRejecting(false)} style={pill("secondary")}>
                    Keep order
                  </button>
                </div>
              ) : (
                <button onClick={() => setRejecting(true)} style={pill("secondary", 52)}>
                  Reject order
                </button>
              ))}
            {editable && !rejecting && (
              <button
                disabled={!allDecided || !checksDone || act.isPending}
                onClick={confirm}
                title={!allDecided ? "Mark every medicine first" : !checksDone ? "Tick every prescription check first" : undefined}
                style={{ ...pill("primary", 52), opacity: allDecided && checksDone ? 1 : 0.5 }}
              >
                {anySubstitute ? "Confirm, send substitute to customer" : "Confirm order"}
              </button>
            )}
            {o.status === "confirmed" && !rejecting && (
              <>
                <Link href={`/slip/${o.orderId}`} style={pill("secondary", 52)}>
                  Print slip
                </Link>
                <button disabled={act.isPending} onClick={() => act.mutate({ path: "ready", body: {} })} style={pill("primary", 52)}>
                  Packed and sealed: ready for rider
                </button>
              </>
            )}
          </div>
          {o.status === "awaiting_customer" && (
            <div style={{ fontSize: 13, color: C.muted }}>Waiting for the customer to answer the substitute (up to 30 minutes).</div>
          )}
        </div>
      </div>

      {subFor && (
        <SubstituteDialog
          orderId={o.orderId}
          pharmacyName={pharmacyName}
          medicineId={subFor.medicineId}
          medicine={subFor.medicine}
          strips={subFor.strips}
          unitPricePaisa={subFor.unitPricePaisa}
          initial={(() => {
            const d = decisions[subFor.lineId];
            return d?.status === "substitute" ? d.chosen : null;
          })()}
          onCancel={() => setSubFor(null)}
          onSend={(chosen) => {
            setDecisions((d) => ({ ...d, [subFor.lineId]: { status: "substitute", chosen } }));
            setSubFor(null);
          }}
        />
      )}
    </div>
  );
}

function LineRow({
  line,
  last,
  editable,
  decision,
  onPick,
}: {
  line: QueueLine;
  last: boolean;
  editable: boolean;
  decision?: Decision;
  onPick: (s: "on_shelf" | "unavailable" | "substitute") => void;
}) {
  const m = line.medicine;
  const units = line.strips * unitsPerStrip(m?.dosageForm ?? "");
  const chosen = decision?.status === "substitute" ? decision.chosen : null;
  const shownSub = chosen
    ? { name: `${chosen.medicine.brandName} ${chosen.medicine.strength}`, price: chosen.unitPricePaisa }
    : line.substitute
      ? { name: [line.substitute.medicine?.brandName, line.substitute.medicine?.strength].filter(Boolean).join(" ") || `#${line.substitute.medicineId}`, price: line.substitute.unitPricePaisa }
      : null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 0", borderBottom: last ? "none" : `1px solid ${C.line}` }}>
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ fontSize: 16, lineHeight: "22px" }}>
          <span style={{ fontWeight: 600 }}>{m?.brandName ?? `Medicine #${line.medicineId}`}</span> {m?.strength} · {line.strips} strip{line.strips === 1 ? "" : "s"}
        </div>
        <div style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>
          {[m?.genericName, m?.dosageForm?.toLowerCase()].filter(Boolean).join(" · ")}
          {units > line.strips ? ` · ${units} total` : ""} · {taka(line.unitPricePaisa)} / strip
        </div>
        {shownSub && (
          <div style={{ fontSize: 13, lineHeight: "18px", color: C.teal, fontWeight: 600, marginTop: 2 }}>
            Substitute: {shownSub.name}
            {shownSub.price !== null ? ` · ${taka(shownSub.price)} / strip` : ""}
          </div>
        )}
      </div>
      {editable ? (
        <div role="group" aria-label={`${m?.brandName ?? "Medicine"} stock`} style={{ display: "flex", gap: 4, background: C.tint, borderRadius: 999, padding: 4 }}>
          {(["on_shelf", "unavailable", "substitute"] as const).map((s) => {
            const on = decision?.status === s;
            return (
              <button
                key={s}
                aria-pressed={on}
                onClick={() => onPick(s)}
                style={{ height: 36, padding: "0 14px", border: "none", borderRadius: 999, background: on ? C.teal : "transparent", color: on ? C.onTeal : C.body, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {s === "on_shelf" ? "On shelf" : s === "unavailable" ? "Unavailable" : "Substitute"}
              </button>
            );
          })}
        </div>
      ) : (
        <span style={{ fontSize: 13, fontWeight: 600, padding: "4px 10px", borderRadius: 999, background: C.tint }}>{lineStatusLabel(line.lineStatus)}</span>
      )}
    </div>
  );
}

const lineStatusLabel = (s: string) =>
  ({ on_shelf: "On shelf", unavailable: "Unavailable", substitute_offered: "Substitute offered", substituted: "Substitute accepted", declined: "Declined", pending: "Not checked" })[s] ?? s;

function explain(code: string): string {
  switch (code) {
    case "every_line_needed":
      return "Mark every medicine first.";
    case "order_changed":
    case "not_waiting_for_confirmation":
      return "Someone else changed this order. It has been reloaded.";
    case "invalid_substitute":
      return "Pick a substitute and its price.";
    default:
      return `Could not save (${code}).`;
  }
}
